import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import { EllipsisHorizontalIcon } from '@heroicons/react/16/solid';
import AppSidebar from './AppSidebar';
import { Navbar } from './common/Catalyst/navbar';
import { SidebarLayout } from './common/Catalyst/sidebar-layout';
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
} from './common/Catalyst/dropdown';
import LoadingSpinner from './LoadingSpinner';
import { amenityService } from './clients/ApiClients';
import {
  AmenityReturn,
  AmenityUpdate,
  ReservationReturn,
} from './clients/rust-api-client';
import { canManageAmenities } from './constants/UserTypes';
import { getUserTypeFromToken } from './utils/TokenUtils';
import { formatMoney } from './utils/billingHelpers';
import {
  AmenityDetailsForm,
  AmenityFeeForm,
  AmenityLimitsForm,
} from './amenity/AmenitySettingsForms';
import { AmenityHoursEditor } from './amenity/AmenityHoursEditor';
import { AmenitySeasons } from './amenity/AmenitySeasons';
import { AmenityAgreements } from './amenity/AmenityAgreements';
import { AmenityClosures } from './amenity/AmenityClosures';
import { AmenityPhotos } from './amenity/AmenityPhotos';
import { ConflictDialog } from './amenity/ConflictDialog';
import {
  AmenityDepositAccessForm,
  AmenityDocumentRequirements,
  AmenityPricing,
  AmenityWaitlist,
  phaseThreeHeaderBits,
} from './amenity/AmenityPhaseThreeTabs';
import {
  APPROVAL_MODE_LABELS,
  BOOKING_MODE_LABELS,
  conflictsFrom,
  formatMinute,
  reservationErrorCode,
  reservationErrorMessage,
} from './reservation/reservationMeta';
import { ErrorAlert } from './common/Custom/ErrorAlert';
import { CreateActions } from './shell/CreateActions';

/**
 * One amenity's setup (#642), as tabs: details and rules, opening hours,
 * seasons, booking limits, fee, pricing rules, deposit and access,
 * required documents, agreement versions, closures, photos, and the
 * waitlist. Board members see every tab read-only.
 *
 * The tab lives in the URL (`?tab=hours`), as on an association's page.
 */
const TABS = [
  { key: 'details', label: 'Details & rules' },
  { key: 'hours', label: 'Hours' },
  { key: 'seasons', label: 'Seasons' },
  { key: 'limits', label: 'Booking limits' },
  { key: 'fee', label: 'Fee' },
  { key: 'pricing', label: 'Pricing' },
  { key: 'access', label: 'Deposit & access' },
  { key: 'documents', label: 'Documents' },
  { key: 'agreement', label: 'Agreement' },
  { key: 'closures', label: 'Closures' },
  { key: 'photos', label: 'Photos' },
  { key: 'waitlist', label: 'Waitlist' },
] as const;

const tabClass =
  'relative -mb-px whitespace-nowrap border-b-2 border-transparent px-1 pb-2.5 text-[13px] font-medium text-fg-muted ' +
  'hover:text-fg focus:outline-none data-[focus]:text-fg data-[selected]:border-accent data-[selected]:text-fg';

const AmenityDetailPage: React.FC = () => {
  const { hoaId, amenityId } = useParams<{ hoaId: string; amenityId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const readOnly = !canManageAmenities(getUserTypeFromToken());
  const [amenity, setAmenity] = useState<AmenityReturn | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [archiveConflicts, setArchiveConflicts] = useState<ReservationReturn[] | null>(null);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!amenityId) return;
    try {
      setAmenity((await amenityService.getAmenity({ amenityId })).data);
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't load the amenity."));
    }
  }, [amenityId]);

  useEffect(() => {
    setLoading(true);
    reload().finally(() => setLoading(false));
  }, [reload]);

  const save = async (update: AmenityUpdate) => {
    if (!amenityId) return;
    try {
      setAmenity((await amenityService.updateAmenity({ amenityId, amenityUpdate: update })).data);
    } catch (err) {
      // STALE_AMENITY: someone saved this amenity after we loaded it (the
      // `expected_date_updated` guard). Load their version, rather than
      // leaving the form on a copy every save will refuse; the form says so
      // through `reservationErrorMessage`, which words that code.
      if (reservationErrorCode(err) === 'STALE_AMENITY') {
        await reload();
      }
      throw err;
    }
  };

  const archive = async (cancelReason?: string) => {
    if (!amenityId) return;
    await amenityService.archiveAmenity({
      amenityId,
      cancelFuture: cancelReason ? true : undefined,
      reason: cancelReason,
    });
    navigate(`/hoa/${hoaId}/amenities`);
  };

  const startArchive = async () => {
    if (!amenity) return;
    if (!window.confirm(`Archive ${amenity.name}? Households will no longer see it.`)) return;
    setError(null);
    try {
      await archive();
    } catch (err) {
      const inTheWay = conflictsFrom(err);
      if (inTheWay) {
        setArchiveError(null);
        setArchiveConflicts(inTheWay);
      } else {
        setError(reservationErrorMessage(err, "Couldn't archive the amenity."));
      }
    }
  };

  if (loading) return <LoadingSpinner />;

  const requested = searchParams.get('tab');
  const tabIndex = Math.max(0, TABS.findIndex((t) => t.key === requested));
  const selectTab = (index: number) => {
    const next = new URLSearchParams(searchParams);
    if (index === 0) next.delete('tab');
    else next.set('tab', TABS[index].key);
    setSearchParams(next, { replace: true });
  };

  return (
    <SidebarLayout
      sidebar={<AppSidebar currentPage="reservations" />}
      navbar={<Navbar>{/* Your navbar content */}</Navbar>}
    >
      <div className="-mx-2 mb-6 flex items-center gap-2 border-b border-line/60 px-2 pb-3 text-[13px]">
        <Link to={`/hoa/${hoaId}/amenities`} className="shrink-0 text-fg-muted hover:text-fg">
          Amenities
        </Link>
        <span className="text-fg-faint" aria-hidden>
          /
        </span>
        <span className="min-w-0 truncate font-medium text-fg">{amenity?.name ?? 'Amenity'}</span>
        <div className="ml-auto flex items-center gap-2">
          {/* A booking of this amenity, where it takes bookings. */}
          {hoaId && amenity && amenity.is_active && !amenity.archived_at &&
            amenity.booking_mode !== 'info_only' && (
              <CreateActions scope={{ hoaId }} reservation={{ amenityId: amenity.id }} />
            )}
          {!readOnly && amenity && !amenity.archived_at && (
            <Dropdown>
              <DropdownButton plain aria-label="More actions">
                <EllipsisHorizontalIcon className="h-4 w-4" />
              </DropdownButton>
              <DropdownMenu anchor="bottom end">
                <DropdownItem onClick={startArchive}>Archive amenity…</DropdownItem>
              </DropdownMenu>
            </Dropdown>
          )}
        </div>
      </div>

      <ErrorAlert>{error}</ErrorAlert>

      {amenity && (
        <>
          <div>
            <h1 className="text-2xl/8 font-semibold tracking-tight text-fg">{amenity.name}</h1>
            <p className="mt-1 text-[13px] text-fg-muted">
              {BOOKING_MODE_LABELS[amenity.booking_mode] ?? amenity.booking_mode}
              {amenity.booking_mode !== 'info_only' && ` · ${APPROVAL_MODE_LABELS[amenity.approval_mode]}`}
              {' · '}
              {amenity.fee_type === 'none' || !amenity.fee_amount
                ? 'Free'
                : `${formatMoney(amenity.fee_amount)}${
                    amenity.fee_type === 'hourly' ? ' an hour' : amenity.fee_type === 'per_night' ? ' a night' : ' a booking'
                  }`}
              {amenity.booking_mode === 'shared_capacity' && amenity.capacity && ` · Up to ${amenity.capacity} at a time`}
              {amenity.booking_mode === 'overnight' &&
                amenity.check_in_minute != null &&
                amenity.check_out_minute != null &&
                ` · Check-in ${formatMinute(amenity.check_in_minute)}, check-out ${formatMinute(amenity.check_out_minute)}`}
              {phaseThreeHeaderBits(amenity).map((b) => ` · ${b}`)}
              {!amenity.is_active && ' · Inactive'}
              {amenity.archived_at && ' · Archived'}
              {readOnly && ' · Read-only'}
            </p>
          </div>

          <TabGroup selectedIndex={tabIndex} onChange={selectTab} className="mt-6">
            <div className="overflow-x-auto border-b border-line">
              <TabList className="flex gap-5" aria-label="Amenity settings">
                {TABS.map((t) => (
                  <Tab key={t.key} className={tabClass}>
                    {t.label}
                  </Tab>
                ))}
              </TabList>
            </div>
            <TabPanels className="pt-6">
              <TabPanel className="focus:outline-none">
                <AmenityDetailsForm amenity={amenity} readOnly={readOnly} onSave={save} />
              </TabPanel>
              <TabPanel className="focus:outline-none">
                <AmenityHoursEditor amenity={amenity} readOnly={readOnly} onSaved={setAmenity} />
              </TabPanel>
              <TabPanel className="focus:outline-none">
                <AmenitySeasons amenity={amenity} readOnly={readOnly} onChanged={reload} />
              </TabPanel>
              <TabPanel className="focus:outline-none">
                <AmenityLimitsForm amenity={amenity} readOnly={readOnly} onSave={save} />
              </TabPanel>
              <TabPanel className="focus:outline-none">
                <AmenityFeeForm amenity={amenity} readOnly={readOnly} onSave={save} />
              </TabPanel>
              <TabPanel className="focus:outline-none">
                <AmenityPricing amenity={amenity} readOnly={readOnly} onSave={save} onChanged={reload} />
              </TabPanel>
              <TabPanel className="focus:outline-none">
                <AmenityDepositAccessForm amenity={amenity} readOnly={readOnly} onSave={save} />
              </TabPanel>
              <TabPanel className="focus:outline-none">
                <AmenityDocumentRequirements amenity={amenity} readOnly={readOnly} onChanged={reload} />
              </TabPanel>
              <TabPanel className="focus:outline-none">
                <AmenityAgreements amenity={amenity} readOnly={readOnly} onPublished={reload} />
              </TabPanel>
              <TabPanel className="focus:outline-none">
                <AmenityClosures amenity={amenity} readOnly={readOnly} onChanged={reload} />
              </TabPanel>
              <TabPanel className="focus:outline-none">
                <AmenityPhotos amenity={amenity} readOnly={readOnly} onChanged={reload} />
              </TabPanel>
              <TabPanel className="focus:outline-none">
                <AmenityWaitlist amenity={amenity} readOnly={readOnly} />
              </TabPanel>
            </TabPanels>
          </TabGroup>

          <ConflictDialog
            open={archiveConflicts !== null}
            action={`archive ${amenity.name}`}
            conflicts={archiveConflicts ?? []}
            error={archiveError}
            onClose={() => setArchiveConflicts(null)}
            onConfirm={async (reason) => {
              try {
                await archive(reason);
              } catch (err) {
                setArchiveError(reservationErrorMessage(err, "Couldn't archive the amenity."));
              }
            }}
          />
        </>
      )}
    </SidebarLayout>
  );
};

export default AmenityDetailPage;
